/**
 * How long a job has spent in each stage.
 *
 * The board already answers "how long has this been HERE" from
 * status_changed_at. This file answers the other question — how fast does this
 * factory actually work — which needs the stages the order has already left,
 * and those only exist since cg_stage_events (20261104000100).
 *
 * ── Why days and not hours ─────────────────────────────────────────────────
 *
 * Nothing here is precise to the hour and pretending otherwise would invite
 * arguments about whether a job took 4.2 days. Manufacturing lead times are
 * counted in days by everyone involved, and the number's job is to create
 * pressure and comparison, not to settle a billing dispute.
 *
 * ── Why estimates are carried, not hidden ──────────────────────────────────
 *
 * Orders that existed before the log was added have history synthesised from
 * the date columns we had. Those intervals are real where a date existed and
 * too long where one did not — an order that passed through Sample Approval
 * leaves no trace of when. Every such row carries isEstimate, and it survives
 * every calculation below, because a number somebody quotes to a client should
 * say when it is a guess.
 */

const DAY_MS = 86_400_000;

export interface StageEvent {
  from_status: string | null;
  to_status: string;
  entered_at: string;
  is_estimate: boolean;
}

export interface StageSpan {
  status: string;
  enteredAt: string;
  /** Whole days spent here. For the current stage, days so far. */
  days: number;
  /** True while the order is still in this stage. */
  isCurrent: boolean;
  /** True when either end of the interval came from the backfill. */
  isEstimate: boolean;
}

/**
 * Whole days between two instants, floored.
 *
 * Floored rather than rounded so a stage entered this morning reads as 0 days
 * rather than 1. Under-reporting is the safe direction: it can only fail to
 * raise an alarm that tomorrow will raise correctly, where over-reporting puts
 * a number in front of a supplier that they can prove wrong.
 */
function wholeDays(fromIso: string, toIso: string): number {
  const from = new Date(fromIso).getTime();
  const to = new Date(toIso).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) {
    return 0;
  }
  return Math.max(0, Math.floor((to - from) / DAY_MS));
}

/**
 * The events, oldest first, with anything unparseable dropped.
 *
 * The database returns them ordered already. Sorting again is not distrust of
 * the query — it is that this function is also called on arrays assembled in
 * tests and on payloads that have been through jsonb, and a duration list that
 * silently depends on its input order is a bug waiting for the first caller
 * who does not know that.
 */
function ordered(events: StageEvent[]): StageEvent[] {
  return events
    .filter((event) => !Number.isNaN(new Date(event.entered_at).getTime()))
    .slice()
    .sort((a, b) => new Date(a.entered_at).getTime() - new Date(b.entered_at).getTime());
}

/**
 * One span per stage the order has been in, oldest first.
 *
 * A stage's duration is the gap to the NEXT event, not to now — that is the
 * whole reason the log exists. The last span runs to `nowIso` and is marked
 * current.
 *
 * An order can enter the same status twice (sent back for revision, moved
 * backwards by an admin). Each visit is its own span rather than being summed,
 * because "Sampling took 20 days" hides that it was 6 days, a rejection, and
 * then 14 — and the rejection is the interesting part.
 */
export function stageSpans(events: StageEvent[], nowIso: string): StageSpan[] {
  const rows = ordered(events);
  return rows.map((event, index) => {
    const next = rows[index + 1];
    const isCurrent = next === undefined;
    return {
      status: event.to_status,
      enteredAt: event.entered_at,
      days: wholeDays(event.entered_at, isCurrent ? nowIso : next.entered_at),
      isCurrent,
      // The span's start OR its end can be synthesised, and either one makes
      // the length approximate.
      isEstimate: event.is_estimate || (next?.is_estimate ?? false),
    };
  });
}

/** Days in the stage the order is in right now, or null when there is no history. */
export function daysInCurrentStage(events: StageEvent[], nowIso: string): number | null {
  const spans = stageSpans(events, nowIso);
  const last = spans[spans.length - 1];
  return last === undefined ? null : last.days;
}

/** The stage the order is in right now, or null when there is no history. */
export function currentStage(events: StageEvent[]): string | null {
  const rows = ordered(events);
  const last = rows[rows.length - 1];
  return last === undefined ? null : last.to_status;
}

export interface Interval {
  days: number;
  isEstimate: boolean;
}

/**
 * Days between first entering one stage and first entering another.
 *
 * FIRST entry on both ends, deliberately. An order that went to Sampling,
 * back, and to Sampling again took the whole of that from the client's point
 * of view — measuring from the second entry would report a cycle time that
 * quietly excludes the rework, which is the number a supplier would most like
 * reported and the one least worth having.
 *
 * Null when either end has not happened. Not zero: an order that has not
 * shipped has no PO-to-ship time, and returning 0 would drag every average
 * down with jobs that simply are not finished.
 */
export function interval(
  events: StageEvent[],
  fromStatus: string,
  toStatus: string,
): Interval | null {
  const rows = ordered(events);
  const start = rows.find((event) => event.to_status === fromStatus);
  if (start === undefined) {
    return null;
  }
  const end = rows.find(
    (event) =>
      event.to_status === toStatus &&
      new Date(event.entered_at).getTime() >= new Date(start.entered_at).getTime(),
  );
  if (end === undefined) {
    return null;
  }
  return {
    days: wholeDays(start.entered_at, end.entered_at),
    isEstimate: start.is_estimate || end.is_estimate,
  };
}

/**
 * The counter as it appears on a card: "Day 3 in Sampling".
 *
 * Day 1 on the day it arrives, not Day 0 — a factory counting its own days
 * counts the first one. That makes this the only place the off-by-one is
 * deliberate, and it is why the label is built here rather than at each call
 * site where somebody would have to remember.
 */
export function stageCounterLabel(events: StageEvent[], nowIso: string): string | null {
  const stage = currentStage(events);
  const days = daysInCurrentStage(events, nowIso);
  if (stage === null || days === null) {
    return null;
  }
  return `Day ${String(days + 1)} in ${stage}`;
}
